"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { useUserSettingsStore } from "@/hooks/stores/schoolSettingsStore"
import { Lock, Trash2, AlertCircle } from "lucide-react"

interface Permission {
  id: string
  name: string
  description: string
}

const AVAILABLE_PERMISSIONS: Permission[] = [
  {
    id: "view_student_data",
    name: "View Student Data",
    description: "Access and view student information and records",
  },
  {
    id: "edit_grades",
    name: "Edit Grades",
    description: "Modify and update student grades and academic records",
  },
  {
    id: "manage_classes",
    name: "Manage Classes",
    description: "Create and manage classes and class schedules",
  },
  {
    id: "manage_users",
    name: "Manage Users",
    description: "Create, edit, and delete user accounts",
  },
  {
    id: "view_analytics",
    name: "View Analytics",
    description: "Access analytics and reporting features",
  },
  {
    id: "manage_permissions",
    name: "Manage Permissions",
    description: "Modify user permissions and access controls",
  },
]

export function AdditionalSettingsTab() {
  const { settings, updatePermissions } = useUserSettingsStore()
  const [permissions, setPermissions] = useState(settings.permissions)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const handlePermissionToggle = (permissionId: string) => {
    const newPermissions = permissions.includes(permissionId)
      ? permissions.filter((p) => p !== permissionId)
      : [...permissions, permissionId]
    setPermissions(newPermissions)
    updatePermissions(newPermissions)
  }

  const handleDeleteAccount = () => {
    // Simulate account deletion
    localStorage.removeItem("user")
    localStorage.removeItem("isLoggedIn")
    window.location.href = "/"
  }

  return (
    <div className="space-y-6">
      {/* Permissions Section */}
      <Card className="p-6 border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
          <Lock className="w-5 h-5" />
          Manage Permissions
        </h3>

        <div className="space-y-3">
          {AVAILABLE_PERMISSIONS.map((permission) => (
            <div
              key={permission.id}
              className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="flex-1">
                <h4 className="font-medium text-foreground">{permission.name}</h4>
                <p className="text-sm text-muted-foreground">{permission.description}</p>
              </div>
              <button
                onClick={() => handlePermissionToggle(permission.id)}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  permissions.includes(permission.id) ? "bg-primary" : "bg-muted"
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    permissions.includes(permission.id) ? "translate-x-6" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </Card>

      {/* Delete Account Section */}
      <Card className="p-6 border border-destructive/20 bg-destructive/5">
        <h3 className="text-lg font-semibold text-destructive mb-2 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          Danger Zone
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Permanently delete your account and all associated data. This action cannot be undone.
        </p>

        {!showDeleteConfirm ? (
          <Button
            onClick={() => setShowDeleteConfirm(true)}
            variant="outline"
            className="border-destructive text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Account
          </Button>
        ) : (
          <div className="p-4 bg-white border border-destructive rounded-lg">
            <p className="text-sm font-medium text-foreground mb-4">
              Are you sure? This will permanently delete your account and all your data.
            </p>
            <div className="flex gap-3">
              <Button onClick={handleDeleteAccount} className="bg-destructive hover:bg-destructive/90 text-white">
                Yes, Delete Account
              </Button>
              <Button
                onClick={() => setShowDeleteConfirm(false)}
                variant="outline"
                className="border-border text-foreground hover:bg-muted"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
